// docs/138 XO4/XO5 — the two new public tables and the one reducer the exploration overhaul adds
// on the client side, behind small typed wrappers:
//
//   wonder_blessing { id, world_id, hero_id, owner, encounter_id, wonder, options, names, blurbs, chosen?, at }
//   haul_gold       { id, world_id, encounter_id, hero_id, gold }
//   choose_wonder_blessing(offer_id: u64, choice: u8)
//
// The `?explore=1` gallery and the harnesses put stand-in rows in `useExploreStandIns` instead of
// a server; the hooks read those when they are set.
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { create } from 'zustand';
import { tables } from '../../bindings';
import { rpc, type CallResult } from '../net/reducers';
import { identityHex } from '../net/connection';

/** One `wonder_blessing` offer: three choices for one hero who beheld the wonder (docs/138 XO5). */
export interface WonderBlessingRow {
  id: bigint; worldId: bigint; heroId: bigint; encounterId: bigint;
  /** The owner's identity, as hex (the wrapper reads `owner` off the row). */
  owner: string;
  /** The wonder's name, as the party knows it. */
  wonder: string;
  /** Three choices: a body blessing, a craft blessing, an enchantment (feature or upgrade ids). */
  options: number[]; names: string[]; blurbs: string[];
  /** The index chosen, once the player has chosen. */
  chosen?: number;
  /** Stand-in rows only (the gallery has no hero rows): the hero's name. */
  heroName?: string;
}

/** One hero's gold picked up in an exploration so far (docs/138 XO4). */
export interface HaulGoldRow { id: bigint; worldId: bigint; encounterId: bigint; heroId: bigint; gold: number }

/** Rows the gallery and harnesses stand in for the server with (nothing = read the server). */
interface StandIns {
  blessings?: WonderBlessingRow[];
  haulGold?: HaulGoldRow[];
  set(p: Partial<Pick<StandIns, 'blessings' | 'haulGold'>>): void;
}
export const useExploreStandIns = create<StandIns>()((set) => ({ set(p) { set(p); } }));

const NONE = -1n;

const useBlessingRows = (worldId: bigint | undefined) => useTable(tables.wonderBlessing.where((r) => r.worldId.eq(worldId ?? NONE)), { enabled: worldId !== undefined })[0];
const useHaulGoldRows = (encounterId: bigint | undefined) => useTable(tables.haulGold.where((r) => r.encounterId.eq(encounterId ?? NONE)), { enabled: encounterId !== undefined })[0];

/** A generated row (camelCase, `owner` an Identity, `chosen` an option) as `WonderBlessingRow`. */
export function blessingRow(r: { [k: string]: unknown }): WonderBlessingRow {
  const owner = r['owner'];
  const chosen = r['chosen'];
  const out: WonderBlessingRow = {
    id: BigInt(r['id'] as bigint), worldId: BigInt(r['worldId'] as bigint), heroId: BigInt(r['heroId'] as bigint), encounterId: BigInt(r['encounterId'] as bigint),
    owner: typeof owner === 'string' ? owner : identityHex(owner as Parameters<typeof identityHex>[0]),
    wonder: String(r['wonder'] ?? ''),
    options: Array.from((r['options'] as ArrayLike<number> | undefined) ?? [], Number),
    names: ((r['names'] as string[] | undefined) ?? []).map(String),
    blurbs: ((r['blurbs'] as string[] | undefined) ?? []).map(String),
  };
  if (chosen !== undefined && chosen !== null) out.chosen = Number(chosen);
  return out;
}

/** My blessing offers in this World still waiting on a choice, oldest first. */
export function useMyOpenBlessings(worldId: bigint | undefined, me: string): WonderBlessingRow[] {
  const stand = useExploreStandIns((s) => s.blessings);
  const rows = useBlessingRows(worldId);
  return useMemo(() => {
    const all = stand ?? rows.map((r) => blessingRow(r as unknown as { [k: string]: unknown }));
    return all.filter((b) => b.owner === me && b.chosen === undefined && (worldId === undefined || b.worldId === worldId)).sort((a, b) => (a.id < b.id ? -1 : 1));
  }, [stand, rows, me, worldId]);
}

/** Gold each hero has picked up in the exploration `encounterId`, by hero id. */
export function useHaulGold(encounterId: bigint | undefined): Map<number, number> {
  const stand = useExploreStandIns((s) => s.haulGold);
  const rows = useHaulGoldRows(encounterId);
  return useMemo(() => {
    const out = new Map<number, number>();
    const all: readonly HaulGoldRow[] = stand ?? rows;
    for (const r of all) if (encounterId === undefined || r.encounterId === encounterId) out.set(Number(r.heroId), (out.get(Number(r.heroId)) ?? 0) + r.gold);
    return out;
  }, [stand, rows, encounterId]);
}

/** docs/138 XO5: choose blessing `choice` (0–2) of offer `offerId`. */
export function chooseWonderBlessing(offerId: bigint, choice: number): Promise<CallResult> {
  const stand = useExploreStandIns.getState();
  if (stand.blessings) {
    stand.set({ blessings: stand.blessings.map((b) => (b.id === offerId ? { ...b, chosen: choice } : b)) });
    return Promise.resolve({ ok: true });
  }
  return rpc.chooseWonderBlessing(offerId, choice);
}
